import type { Feature } from 'geojson';
import { TrafikenConfig } from '../TrafikenConfig';
import { AjaxSource } from './AjaxSource';
import type { AjaxSourceOptions } from './Sources.types';

const defaultOptions = (region) => ({
    id: 'travel-time-v2-route-source',
    url: `${TrafikenConfig.infoLayerServiceBaseUrl}/api/v2/traveltime/action/getsections?region=${region}`,
});

interface TravelTimeSectionV2Metadata {
    replaceAll: boolean;
    syncCursor: string;
}

interface TravelTimeV2SectionResponse {
    metadata?: TravelTimeSectionV2Metadata;
    data: TravelTimeV2SectionChange[];
}
interface TravelTimeV2Section {
    id: string;
    zoomLevel: number;
    regionCode?: number;
    shape: [number, number][];
}

interface TravelTimeV2SectionDeletion {
    id: string;
    deleted: true;
}

type TravelTimeV2SectionChange = TravelTimeV2Section | TravelTimeV2SectionDeletion;

function isDeletedSection(section: TravelTimeV2SectionChange): section is TravelTimeV2SectionDeletion {
    return 'deleted' in section && section.deleted;
}

interface CachedSections {
    syncCursor: string;
    sections: TravelTimeV2Section[];
}

const cacheDatabaseName = 'trafiken-map-cache';
const cacheStoreName = 'travel-time-sections';

function isCachedSections(value: unknown): value is CachedSections {
    return (
        typeof value === 'object' &&
        value !== null &&
        'syncCursor' in value &&
        typeof value.syncCursor === 'string' &&
        'sections' in value &&
        Array.isArray(value.sections)
    );
}

/**
 * Geometry source backed by the TomTom-fed getsections endpoint. The
 * endpoint returns a lightweight [longitude, latitude] shape per segment (GeoJSON
 * axis order) to minimize payload size; this source maps it straight into GeoJSON
 * LineString features so RoadStatusLayer can render them. Status is joined from
 * TravelTimeV2StatusSource by the segment id.
 */
export class TravelTimeV2SectionSource extends AjaxSource {
    private static cacheDatabase?: Promise<IDBDatabase>;
    private readonly cacheKey: string;
    private readonly cacheHydrated: Promise<void>;
    private readonly sectionsById = new Map<string, TravelTimeV2Section>();
    private syncCursor?: string;
    private request?: { isDelta: boolean };

    constructor(region: string, options?: Partial<AjaxSourceOptions>) {
        super({ ...defaultOptions(region), ...options });
        this.cacheKey = `travel-time-sections-v1-${region.toLowerCase()}`;
        this.cacheHydrated = this.hydrateCache();
    }

    async getAjaxPromise(): Promise<unknown> {
        await this.cacheHydrated;

        const isDelta = this.syncCursor !== undefined;
        const url = new URL(this.options.url, window.location.origin);
        if (isDelta) {
            url.searchParams.set('syncCursor', this.syncCursor);
        }

        this.request = { isDelta };
        return super.getAjaxPromise(url.toString());
    }

    parseData(response: TravelTimeV2SectionResponse | TravelTimeV2SectionChange[]): Feature[] {
        const request = this.request;
        if (!request) {
            return [];
        }

        const data = Array.isArray(response) ? response : response.data;
        const replaceAll = !request.isDelta || (!Array.isArray(response) && response.metadata?.replaceAll === true);
        if (replaceAll) {
            this.sectionsById.clear();
        }

        data.forEach((section) => {
            if (isDeletedSection(section)) {
                this.sectionsById.delete(section.id);
            } else {
                this.sectionsById.set(section.id, section);
            }
        });

        if (!Array.isArray(response) && response.metadata?.syncCursor) {
            this.syncCursor = response.metadata.syncCursor;
        }
        this.persistCache();
        return this.toFeatures([...this.sectionsById.values()]);
    }

    private async hydrateCache(): Promise<void> {
        const cached = await this.getCachedSections();
        if (!cached) {
            return;
        }

        cached.sections.forEach((section) => this.sectionsById.set(section.id, section));
        this.syncCursor = cached.syncCursor;
        this._data = this.toFeatures(cached.sections);
        this.persistCache();
    }

    private async getCachedSections(): Promise<CachedSections | undefined> {
        try {
            const database = await this.getCacheDatabase();
            const transaction = database.transaction(cacheStoreName, 'readonly');
            const request = transaction.objectStore(cacheStoreName).get(this.cacheKey);
            const cached = await new Promise<unknown>((resolve, reject) => {
                request.onsuccess = () => resolve(request.result);
                request.onerror = () => reject(request.error);
            });

            return isCachedSections(cached) ? cached : undefined;
        } catch (error) {
            console.warn('Unable to read cached travel-time sections:', error);
            return undefined;
        }
    }

    private persistCache(): void {
        void this.persistCacheAsync();
    }

    private async persistCacheAsync(): Promise<void> {
        try {
            const database = await this.getCacheDatabase();
            const transaction = database.transaction(cacheStoreName, 'readwrite');
            transaction.objectStore(cacheStoreName).put(
                {
                    syncCursor: this.syncCursor,
                    sections: [...this.sectionsById.values()],
                },
                this.cacheKey,
            );
            await new Promise<void>((resolve, reject) => {
                transaction.oncomplete = () => resolve();
                transaction.onerror = () => reject(transaction.error);
                transaction.onabort = () => reject(transaction.error);
            });
        } catch (error) {
            console.warn(`Unable to cache travel-time sections for ${this.cacheKey}:`, error);
        }
    }

    private getCacheDatabase(): Promise<IDBDatabase> {
        TravelTimeV2SectionSource.cacheDatabase ??= new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(cacheDatabaseName, 2);
            request.onupgradeneeded = () => {
                request.result.createObjectStore(cacheStoreName);
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });

        return TravelTimeV2SectionSource.cacheDatabase;
    }

    private toFeatures(sections: TravelTimeV2Section[]): Feature[] {
        return this.safeMapFeatures(sections, (section) => {
            const feature: Feature = {
                type: 'Feature',
                id: section.id,
                geometry: {
                    type: 'LineString',
                    coordinates: section.shape,
                },
                properties: {
                    id: section.id,
                    zoomLevel: section.zoomLevel,
                    regionCode: section.regionCode,
                },
            };
            return feature;
        });
    }
}
